import { Alert, Box, Stack, Typography } from '@mui/material';

import type {
  BillingPlanOfferDto,
  CompanyBillingSummaryDto,
  StripeUpgradePreviewDto,
} from '@shared/services/apiService';

import { billingDate, formatBillingMoney, formatCreditUnits } from '../model/formatBillingUnits';

export const BillingUpgradeDetails = ({
  preview,
  summary,
  nextPlan,
}: {
  preview: StripeUpgradePreviewDto;
  summary?: CompanyBillingSummaryDto;
  nextPlan?: BillingPlanOfferDto;
}) => {
  const rows = [
    ['Current plan', summary?.plan?.key ?? 'Current company plan'],
    ['Requested plan', nextPlan?.key ?? 'Requested company plan'],
    [
      'Estimated amount due now',
      formatBillingMoney(preview.amountDueMinor, preview.currency, preview.currencyScale),
    ],
    ...(nextPlan
      ? [
          [
            'Next monthly amount',
            `${formatBillingMoney(nextPlan.amountMinor, nextPlan.currency, nextPlan.currencyScale)} / month`,
          ],
        ]
      : []),
    [
      'Additional credits after verified payment',
      formatCreditUnits(preview.upgradeUnits, preview.creditScale),
    ],
    ...(summary?.balance && summary.plan
      ? [
          [
            'Already spent',
            `${formatCreditUnits(summary.balance.spentUnits, summary.plan.creditScale)} credits · preserved`,
          ],
          [
            'Reserved for calls',
            `${formatCreditUnits(summary.balance.heldUnits, summary.plan.creditScale)} credits · preserved`,
          ],
        ]
      : []),
    ...(nextPlan
      ? [['Simultaneous calls after activation', String(nextPlan.concurrentLimit)]]
      : []),
    ['Preview expires', billingDate(preview.expiresAt)],
  ];
  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">
        Your current plan stays effective until the payment and subscription change are confirmed.
      </Typography>
      <Box component="dl" sx={{ m: 0 }}>
        {rows.map(([label, value], index) => (
          <Box
            key={label}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
              p: 1.5,
              bgcolor: index % 2 === 0 ? 'action.hover' : 'transparent',
            }}>
            <Typography component="dt" variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              fontWeight={600}
              sx={{ m: 0, overflowWrap: 'anywhere' }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      <Alert severity="info">
        Payment and the effective subscription change must both be confirmed. Existing spending and
        active calls are preserved. Monetary proration and additional credits are calculated
        separately. The credit balance does not reset.
      </Alert>
    </Stack>
  );
};
